import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listServiceRequests,
  getServiceRequestFilters,
  getCalendarEvents,
  getServiceRequest,
  createServiceRequestWithRelations,
} from "api/serviceRequests";
import { serviceRequestFromJson } from "schemas/service_request";
import { mapPatients } from "store/request/utils";
import { queryKeys } from "./keys";
import { CACHE } from "config/cachePolicy";

export function useCalendarEvents(body, options = {}) {
  return useQuery({
    queryKey: queryKeys.serviceRequests.calendar(body),
    queryFn: async () => {
      const response = await getCalendarEvents(body, {
        useLoading: !body?.modeLoading,
      });
      return response;
    },
    staleTime: CACHE.LIVE,
    ...options,
  });
}

export function useServiceRequest(uuid, options = {}) {
  const { useLoading = true, ...queryOptions } = options;
  return useQuery({
    queryKey: queryKeys.serviceRequests.detail(uuid),
    queryFn: async () => {
      const response = await getServiceRequest(uuid, { useLoading });
      return response;
    },
    enabled: !!uuid,
    staleTime: CACHE.LIVE,
    ...queryOptions,
  });
}

export function mapServiceRequestWithVisitTypes(response, visitTypes) {
  if (!response?.data) return response;
  return {
    ...response,
    data: mapPatients({
      request: response.data,
      visitTypes: visitTypes || [],
    }),
  };
}

export function getMappedServiceRequestFromCache(queryClient, uuid) {
  const response = queryClient.getQueryData(
    queryKeys.serviceRequests.detail(uuid),
  );
  if (!response) return undefined;
  const visitTypes = queryClient.getQueryData(queryKeys.visitTypes.all) || [];
  return mapServiceRequestWithVisitTypes(response, visitTypes);
}

/** Loads a service request with visit-type mapping applied to `details`. */
export function useServiceRequestDetails(uuid, queryOptions = {}) {
  const queryClient = useQueryClient();
  const query = useServiceRequest(uuid, queryOptions);

  const details = useMemo(() => {
    if (!query.data) return undefined;
    const visitTypes = queryClient.getQueryData(queryKeys.visitTypes.all) || [];
    return mapServiceRequestWithVisitTypes(query.data, visitTypes);
  }, [query.data, queryClient]);

  return { ...query, details };
}

export function invalidateServiceRequestCalendar(queryClient) {
  return queryClient.invalidateQueries({
    queryKey: queryKeys.serviceRequests.all,
  });
}

export async function fetchEmrServiceRequestsPage({
  filters = {},
  data = { results: [] },
} = {}) {
  const response = await listServiceRequests(filters, {
    useLoading: false,
  });
  if (!response?.data?.results) {
    throw new Error("Invalid EMR service request list response");
  }
  return {
    ...response.data,
    results: (data.results || []).concat(response.data.results),
  };
}

export async function fetchEmrServiceRequestFilters(
  body,
  { useLoading = true } = {},
) {
  const response = await getServiceRequestFilters(body, { useLoading });
  const filters = {};
  for (let key in response?.data) {
    filters[key] = response.data[key].map((i) => ({
      value: i.uuid,
      label: i.name || "N/A",
    }));
  }
  return filters;
}

export function useEmrServiceRequests(filters, options = {}) {
  return useQuery({
    queryKey: queryKeys.serviceRequests.list(filters),
    queryFn: () => fetchEmrServiceRequestsPage({ filters }),
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
}

export function useEmrServiceRequestFilters(body, options = {}) {
  return useQuery({
    queryKey: queryKeys.serviceRequests.emrFilters(body),
    queryFn: () => fetchEmrServiceRequestFilters(body, { useLoading: false }),
    staleTime: CACHE.SHORT,
    ...options,
  });
}

export async function createServiceRequest(payload) {
  const response = await createServiceRequestWithRelations(payload);
  return serviceRequestFromJson(response.data);
}

export function useCreateServiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createServiceRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.all,
      });
    },
  });
}
